import { useEffect } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';

import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_TRANSACTION_LIMITS_QUERY } from '#client/queries';

/**
 * Function convert cent to usd with comma and dot
 *
 * @param {number} number - The cent money - 100000000 cent
 * @returns {string} - The usd money - 10,000,000$
 *
 */
export const formatMoney = (number) => {
  if (typeof number !== 'number') {
    return '';
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currency: 'USD'
  });

  return formatter.format(number / 100);
};

export default function useAccountLimits() {
  const { createCommonErrorToast } = useToastContext();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { error, errors, loading, data } = useQuery(GET_TRANSACTION_LIMITS_QUERY, {
    variables: {
      input: {
        businessUuid: businessEntityUuid,
        partnerAccountId: unitAccountId
      },
      businessEntityUuid
    },
    skip: !unitAccountId,
    noThrow: true
  });

  useLogError(errors, error);

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const {
    dailyDebit: achDailyDebitLimit,
    dailyCredit: achDailyCreditLimit,
    monthlyDebit: achMonthlyDebitLimit,
    monthlyCredit: achMonthlyCreditLimit
  } = data?.partnerAccountLimits?.achLimits || {};

  const { dailyTransferLimit, monthlyTransferLimit, perTransferLimit, minimumTransferLimit } =
    data?.instantFundingAccounts?.[0]?.transactionMetrics || {};

  const { daily: checkDepositDailyLimit, monthly: checkDepositMonthlyLimit } =
    data?.partnerAccountLimits?.checkDepositLimits || {};

  const { dailySent: checkPaymentDailyLimit, monthlySent: checkPaymentMonthlyLimit } =
    data?.partnerAccountLimits?.checkPaymentLimits || {};

  const {
    dailyCardTransaction: dailyCardTransactionLimit,
    dailyDeposit: dailyCardDepositLimit,
    dailyPurchase: dailyCardPurchaseLimit,
    dailyWithdrawal: dailyCardWithdrawalLimit
  } = data?.partnerAccountLimits?.cardLimits || {};

  return {
    loading: loading || !data,
    achDailyDebitLimit: formatMoney(achDailyDebitLimit),
    achDailyCreditLimit: formatMoney(achDailyCreditLimit),
    achMonthlyDebitLimit: formatMoney(achMonthlyDebitLimit),
    achMonthlyCreditLimit: formatMoney(achMonthlyCreditLimit),
    instantDepositDailyTransferLimit: formatMoney(dailyTransferLimit?.amount),
    instantDepositMonthlyTransferLimit: formatMoney(monthlyTransferLimit?.amount),
    instantDepositPerTransferLimit: formatMoney(perTransferLimit?.amount),
    instantDepositMinimumTransferLimit: formatMoney(minimumTransferLimit?.amount),
    checkDepositDailyLimit: formatMoney(checkDepositDailyLimit),
    checkDepositMonthlyLimit: formatMoney(checkDepositMonthlyLimit),

    checkPaymentDailyLimit: formatMoney(checkPaymentDailyLimit),
    checkPaymentMonthlyLimit: formatMoney(checkPaymentMonthlyLimit),

    dailyCardTransactionLimit: formatMoney(dailyCardTransactionLimit),
    dailyCardDepositLimit: formatMoney(dailyCardDepositLimit),
    dailyCardPurchaseLimit: formatMoney(dailyCardPurchaseLimit),
    dailyCardWithdrawalLimit: formatMoney(dailyCardWithdrawalLimit)
  };
}
